import { useTranslation } from 'react-i18next';
import { href, Link, Navigate } from 'react-router';

import { Button } from '@valwin/facto/components';
import { MonitorIcon } from '@valwin/facto/icons';

import { useCurrentPharmacy, useSession } from '~entities/session';
import {
  InstagramShareFeedbackListener,
  InstagramShareHost,
} from '~features/integrations';
import { NotificationsManager } from '~features/notifications';
import { useGetSummary, useInvalidateSummary } from '~features/summary';
import { ROUTES, usePharmacyId } from '~shared/routes';
import { AuthenticatedLayout } from '~widgets/authenticated-layout';

import { useIntercomInit } from '../intercom';
import { Loading } from './Loading';

export function AuthenticatedRoute() {
  const pharmacyId = usePharmacyId();

  const { session, isPending, isError } = useSession(pharmacyId);

  useGetSummary(pharmacyId, { enabled: !!session?.user });

  const invalidateSummary = useInvalidateSummary(pharmacyId);

  useIntercomInit({
    profile: session?.profile,
    pharmacy: session?.pharmacy,
  });

  if (isPending) {
    return <Loading />;
  }

  if (isError || !session?.user) {
    return <Navigate to={href(ROUTES.auth.login, { pharmacyId })} replace />;
  }

  return (
    <>
      <WebsiteLink />
      <NotificationsManager onNotificationHandled={invalidateSummary} />
      <InstagramShareFeedbackListener pharmacyId={pharmacyId} />
      <InstagramShareHost pharmacyId={pharmacyId} />
      <AuthenticatedLayout />
    </>
  );
}

function WebsiteLink() {
  const { t } = useTranslation();

  const pharmacy = useCurrentPharmacy();

  if (!pharmacy.urlOpt) {
    return null;
  }

  return (
    <Button
      size="xl"
      asChild
      position="fixed"
      bottom="20px"
      right="80px"
      zIndex={89}
      borderRadius="100px"
      color="brand.contrast"
      background="brand.solid"
      transition="transform 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00)"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        bg: 'brand.solid',
        borderRadius: 'inherit',
        transition: 'transform 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00)',
        zIndex: -1,
      }}
      _hover={{
        background: 'brand.focusRing',
        color: 'brand.contrast',
        textDecoration: 'none',
        _before: { transform: 'scale(1.1)' },
      }}
    >
      <Link target="_blank" rel="noreferrer" to={pharmacy.urlOpt}>
        <MonitorIcon />
        {t('common mySite')}
      </Link>
    </Button>
  );
}
